import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, Plus, Trash2, Calendar, Clock, Building2, Pencil } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

const emptyForm = { name: "", description: "", price: 0, duration_months: 1, duration_days: "", active: true, unit_ids: [], all_units: true, max_installments: 1 };

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [p, u] = await Promise.all([api.get("/plans"), api.get("/units")]);
    setPlans(p.data);
    setUnits(u.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      description: p.description || "",
      price: p.price ?? 0,
      duration_months: p.duration_months ?? 1,
      duration_days: p.duration_days || "",
      active: p.active ?? true,
      unit_ids: p.unit_ids || [],
      all_units: !p.unit_ids || p.unit_ids.length === 0,
      max_installments: p.max_installments ?? 1,
    });
    setOpen(true);
  };

  const toggleUnit = (uid) => {
    setForm((f) => {
      const has = f.unit_ids.includes(uid);
      return { ...f, unit_ids: has ? f.unit_ids.filter(x => x !== uid) : [...f.unit_ids, uid] };
    });
  };

  const save = async () => {
    try {
      if (!form.name.trim()) return toast.error("Informe o nome do plano");
      if (!form.all_units && form.unit_ids.length === 0) return toast.error("Selecione ao menos uma unidade ou marque 'Todas as unidades'");
      const body = {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price) || 0,
        duration_months: parseInt(form.duration_months) || 0,
        active: form.active,
        unit_ids: form.all_units ? [] : form.unit_ids,
        max_installments: Math.max(1, Math.min(24, parseInt(form.max_installments) || 1)),
      };
      if (form.duration_days && parseInt(form.duration_days) > 0) body.duration_days = parseInt(form.duration_days);
      if (editingId) {
        await api.patch(`/plans/${editingId}`, body);
        toast.success("Plano atualizado");
      } else {
        await api.post("/plans", body);
        toast.success("Plano criado");
      }
      setOpen(false); setEditingId(null); setForm(emptyForm);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover plano?")) return;
    await api.delete(`/plans/${id}`); toast.success("Removido"); load();
  };

  const unitName = (uid) => units.find(u => u.id === uid)?.name || "?";

  return (
    <div className="space-y-4">
      <PageHeader
        title="Planos"
        subtitle={`${plans.length} planos cadastrados · Vincule cada plano à(s) unidade(s) onde ele está disponível`}
        actions={
          <Button onClick={openNew} data-testid="plan-new-button" className="bg-rose-600 hover:bg-rose-700 font-bold uppercase">
            <Plus className="w-4 h-4 mr-2"/>Novo plano
          </Button>
        }
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? "Editar plano" : "Novo plano"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <div><Label>Nome</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="plan-name-input"/></div>
            <div><Label>Descrição</Label><Textarea value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} rows={2}/></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Preço (R$)</Label><Input type="number" step="0.01" value={form.price} onChange={(e)=>setForm({...form,price:e.target.value})} data-testid="plan-price-input"/></div>
              <div><Label>Meses</Label><Input type="number" value={form.duration_months} onChange={(e)=>setForm({...form,duration_months:e.target.value})}/></div>
              <div><Label>Dias (opcional)</Label><Input type="number" value={form.duration_days || ""} onChange={(e)=>setForm({...form,duration_days:e.target.value})} data-testid="plan-days-input" placeholder="1 = diária"/></div>
            </div>
            <div>
              <Label>Máx. parcelas no cartão</Label>
              <Input type="number" min="1" max="24" value={form.max_installments} onChange={(e)=>setForm({...form,max_installments:e.target.value})} data-testid="plan-max-installments-input" placeholder="Ex: 12"/>
              <p className="text-xs text-slate-500 mt-1">Será exibido como "até {Math.max(1, parseInt(form.max_installments) || 1)}x" nos checkouts de cartão (site de vendas e portal). <strong>1 = somente à vista.</strong></p>
            </div>
            <p className="text-xs text-slate-500">Dica: para plano diário/passe único, informe apenas <strong>Dias = 1</strong> (deixe Meses em 0).</p>

            <div className="border-t border-slate-200 pt-3">
              <Label className="flex items-center gap-2 mb-2"><Building2 className="w-4 h-4 text-rose-600"/>Unidades onde o plano é válido</Label>
              <label className="flex items-center gap-2 py-1.5 cursor-pointer" data-testid="plan-all-units-toggle">
                <Checkbox checked={form.all_units} onCheckedChange={(v)=>setForm({...form, all_units: !!v, unit_ids: v ? [] : form.unit_ids})}/>
                <span className="text-sm font-semibold text-slate-800">Todas as unidades</span>
              </label>
              {!form.all_units && (
                <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto pl-1 mt-1 border border-slate-200 rounded-lg p-2">
                  {units.map(u => (
                    <label key={u.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 px-1 rounded" data-testid={`plan-unit-${u.id}`}>
                      <Checkbox checked={form.unit_ids.includes(u.id)} onCheckedChange={()=>toggleUnit(u.id)}/>
                      <span className="text-sm text-slate-700">{u.name}</span>
                    </label>
                  ))}
                  {units.length === 0 && <div className="text-xs text-slate-400 py-2 text-center">Nenhuma unidade cadastrada</div>}
                </div>
              )}
            </div>

            <Button onClick={save} data-testid="plan-save-button" className="w-full bg-rose-600 hover:bg-rose-700 font-bold uppercase">
              {editingId ? "Salvar alterações" : "Criar plano"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-list">
        {plans.map(p => {
          const isDaily = p.duration_days && p.duration_days > 0;
          const planUnits = p.unit_ids || [];
          const allUnits = planUnits.length === 0;
          return (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-6 hover:border-rose-300 hover:shadow-lg transition-all group relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-rose-500/5 group-hover:bg-rose-500/10 transition-colors"/>
              <div className="flex items-start justify-between relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center shadow-md">
                  <CreditCard className="w-6 h-6 text-white"/>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" onClick={()=>openEdit(p)} data-testid={`plan-edit-${p.id}`}><Pencil className="w-4 h-4 text-slate-500"/></Button>
                  <Button variant="ghost" size="icon" onClick={()=>remove(p.id)} data-testid={`plan-delete-${p.id}`}><Trash2 className="w-4 h-4 text-rose-400"/></Button>
                </div>
              </div>
              <h3 className="font-heading text-xl font-extrabold text-slate-900 mt-4 uppercase tracking-tight">{p.name}</h3>
              {p.description && <p className="text-sm text-slate-500 mt-1 min-h-[40px]">{p.description}</p>}
              <div className="mt-5 pb-2 border-b border-dashed border-slate-200">
                <div className="flex items-baseline gap-1">
                  <span className="text-sm text-slate-400 font-semibold">R$</span>
                  <span className="font-heading text-4xl font-extrabold text-rose-600">{p.price.toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 font-medium">
                {isDaily ? <Clock className="w-4 h-4"/> : <Calendar className="w-4 h-4"/>}
                <span>{isDaily ? `${p.duration_days} dia${p.duration_days > 1 ? "s" : ""}` : `${p.duration_months} m\u00EAs${p.duration_months > 1 ? "es" : ""}`}</span>
                {(p.max_installments || 1) > 1 && (
                  <span className="ml-auto px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase">até {p.max_installments}x</span>
                )}
              </div>
              <div className="mt-3 flex items-start gap-1.5 flex-wrap">
                <Building2 className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0"/>
                {allUnits ? (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] font-bold uppercase">Todas as unidades</Badge>
                ) : (
                  planUnits.map(uid => (
                    <Badge key={uid} className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px] font-semibold">{unitName(uid)}</Badge>
                  ))
                )}
              </div>
            </div>
          );
        })}
        {plans.length === 0 && (
          <div className="col-span-full bg-white rounded-xl border border-dashed border-slate-300 py-16 text-center">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-2"/>
            <p className="text-slate-500 font-semibold">Nenhum plano cadastrado</p>
            <p className="text-xs text-slate-400 mt-1">Clique em "Novo plano" para começar</p>
          </div>
        )}
      </div>
    </div>
  );
}
